const toBarcodeString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const computeProductBarcodes = (product) => {
  const barcodes = new Set();
  const addBarcode = (value) => {
    const barcode = toBarcodeString(value);
    if (barcode) {
      barcodes.add(barcode);
    }
  };

  addBarcode(product.barcode);

  if (Array.isArray(product.eans)) {
    product.eans.forEach((item) => {
      if (typeof item === 'string' || typeof item === 'number') {
        addBarcode(item);
        return;
      }

      addBarcode(item?.ean);
      addBarcode(item?.barcode);
    });
  }

  return Array.from(barcodes);
};

const barcodeCache = new WeakMap();

const getBarcodeRecord = (product) => {
  let record = barcodeCache.get(product);
  if (!record) {
    const list = computeProductBarcodes(product);
    record = { list, text: list.join(' ') };
    barcodeCache.set(product, record);
  }
  return record;
};

export const getProductBarcodes = (product) => {
  if (!product) return [];
  if (typeof product !== 'object') return computeProductBarcodes(product);
  return getBarcodeRecord(product).list;
};

export const getProductBarcodeSearchText = (product) => {
  if (!product || typeof product !== 'object') {
    return computeProductBarcodes(product).join(' ');
  }
  return getBarcodeRecord(product).text;
};

export const getMatchingProductBarcode = (product, searchValue = '') => {
  const barcodes = getProductBarcodes(product);
  const fallback = toBarcodeString(product?.barcode) || barcodes[0] || '';
  const searchTerm = toBarcodeString(searchValue).toLowerCase();

  if (!searchTerm || barcodes.length === 0) {
    return fallback;
  }

  if (/^\d{1,3}$/.test(searchTerm)) {
    return (
      barcodes.find((barcode) => {
        if (barcode.length < 3) return false;

        const last3Digits = barcode.slice(-3);
        if (searchTerm.length === 1) {
          return last3Digits.charAt(2) === searchTerm;
        }
        if (searchTerm.length === 2) {
          return last3Digits.slice(-2) === searchTerm;
        }
        return last3Digits === searchTerm;
      }) || fallback
    );
  }

  const normalizedBarcodes = barcodes.map((barcode) => ({
    barcode,
    normalized: barcode.toLowerCase(),
  }));

  const exactMatch = normalizedBarcodes.find(
    ({ normalized }) => normalized === searchTerm,
  );
  if (exactMatch) return exactMatch.barcode;

  const wholeSearchMatch = normalizedBarcodes.find(({ normalized }) =>
    normalized.includes(searchTerm),
  );
  if (wholeSearchMatch) return wholeSearchMatch.barcode;

  const searchParts = searchTerm.match(/\w+/g) || [];
  const partMatch = normalizedBarcodes.find(({ normalized }) =>
    searchParts.some((part) => part.length >= 2 && normalized.includes(part)),
  );

  return partMatch?.barcode || fallback;
};
